import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Stethoscope, 
  Award, 
  Hospital, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Activity,
  HeartPulse,
  Shield,
  GraduationCap,
  Users,
  Microscope,
  ChevronRight,
  Video,
  Calendar,
  BookOpen
} from 'lucide-react';
import { doctorInfo, procedures, stats, faqs, workingHours, diseases, blogArticles } from '../mockData';
import StatsCounter from '../components/StatsCounter';
import ConsultationPreparation from '../components/ConsultationPreparation';
import ContactForm from '../components/ContactForm';
import AppointmentBooking from '../components/AppointmentBooking';

const NEW_HERO_IMAGE = 'https://customer-assets-4nw71qhi.emergentagent.net/job_8c1ac2a9-550d-4720-aa85-fb2d50afdb96/artifacts/mlftkum2_ChatGPT%20Image%20Aug%2023%2C%202026%2C%2008_24_26%20PM.png';

const HomePage = () => {
  const handleBookConsultation = () => {
    document.getElementById('book-appointment')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${doctorInfo.whatsapp}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
                <GraduationCap className="w-3 h-3 mr-1" />
                DM Pulmonary, Critical Care and Sleep Medicine from AIIMS, New Delhi
              </Badge>
              <h1 className="text-5xl font-bold text-gray-900 dark:text-slate-50 mb-4">
                {doctorInfo.name}
              </h1>
              <p className="text-xl text-gray-600 dark:text-slate-300 mb-3">
                {doctorInfo.specialization}
              </p>
              <p className="text-lg text-gray-700 dark:text-slate-300 mb-6">
                {doctorInfo.qualification}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white"
                  onClick={handleBookConsultation}
                  data-testid="hero-book-consultation-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  onClick={handleWhatsAppClick}
                >
                  WhatsApp
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl dark:shadow-slate-900/50">
                <img 
                  src={NEW_HERO_IMAGE}
                  alt="Dr. Akshay Kawedia"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 bg-blue-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-6">
              About Dr. Akshay Kawedia
            </h2>
            <p className="text-lg text-gray-700 dark:text-slate-300 leading-relaxed mb-6">
              Dr. Akshay Kawedia is a leading Pulmonologist, Chest Specialist, Critical Care and Sleep Medicine Expert in Pune. 
              He secured <span className="font-semibold text-blue-600 dark:text-blue-400">All India Rank 1 in the INI Superspeciality (INI-SS) Examination</span> and 
              completed his DM in Pulmonary, Critical Care and Sleep Medicine from <span className="font-semibold">AIIMS New Delhi</span>.
            </p>
            <p className="text-lg text-gray-700 dark:text-slate-300 leading-relaxed mb-6">
              He earned his <span className="font-semibold">MBBS from BJ Government Medical College, Pune</span> and 
              <span className="font-semibold"> MD in General Medicine from Seth GS Medical College and KEM Hospital, Mumbai</span>. 
              With over 8 years of clinical experience, Dr. Kawedia specializes in the diagnosis and treatment of Asthma, COPD, 
              Tuberculosis (TB), Interstitial Lung Disease (ILD), Bronchiectasis, Lung Infections, Sleep Apnea, Snoring Disorders, 
              and Critical Care conditions.
            </p>
            <p className="text-lg text-gray-700 dark:text-slate-300 leading-relaxed mb-6">
              As an Interventional Pulmonologist, he is trained in advanced procedures including Bronchoscopy, EBUS (Endobronchial Ultrasound), 
              Pleural Procedures, and Airway Interventions. Dr. Kawedia is committed to delivering evidence-based, patient-centered care with a 
              focus on accurate diagnosis, personalized treatment, and long-term respiratory health.
            </p>
            <p className="text-lg text-gray-700 dark:text-slate-300 leading-relaxed">
              Patients seeking a Pulmonologist, Lung Specialist, Asthma Specialist, Sleep Apnea Specialist, or Chest Physician in Pune 
              trust Dr. Akshay Kawedia for his academic excellence, advanced expertise, and compassionate approach to care.
            </p>
          </div>
        </div>
      </section>

      {/* Combined Services & Conditions Section */}
      <section id="services" className="py-20 px-6 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
              <Stethoscope className="w-3 h-3 mr-1" />
              Expert Care
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Services & Conditions We Treat
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg max-w-3xl mx-auto">
              Comprehensive diagnosis and treatment for all respiratory conditions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {diseases.map((disease) => (
              <Link key={disease.id} to={`/conditions/${disease.id}`}>
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group">
                  <div className="h-36 overflow-hidden rounded-t-lg">
                    <img 
                      src={disease.image} 
                      alt={disease.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-gray-900 dark:text-slate-50 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{disease.title}</h3>
                    <p className="text-gray-600 dark:text-slate-400 text-sm line-clamp-2">{disease.shortDesc}</p>
                    <div className="mt-3 flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:text-blue-700 dark:group-hover:text-blue-300">
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Interventional Procedures */}
      <section className="py-20 px-6 bg-blue-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
              <Microscope className="w-3 h-3 mr-1" />
              Advanced Procedures
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Interventional Pulmonology
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg max-w-3xl mx-auto">
              Minimally invasive procedures for accurate diagnosis and effective treatment of complex lung and airway diseases
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {procedures.map((procedure) => (
              <Card key={procedure.id} className="text-center hover:shadow-lg transition-shadow duration-300 bg-white dark:bg-slate-800">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Microscope className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <CardTitle className="text-lg">{procedure.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 dark:text-slate-400 text-sm">
                    {procedure.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Stats Counter */}
      <StatsCounter />

      {/* Health Blog Preview */}
      <section className="py-20 px-6 bg-blue-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
              <BookOpen className="w-3 h-3 mr-1" />
              Health Tips
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Health Articles & Tips
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg max-w-3xl mx-auto">
              Expert insights on respiratory health and wellness
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogArticles.slice(0, 3).map((article) => (
              <Link key={article.id} to={`/blog/${article.id}`}>
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="h-40 overflow-hidden rounded-t-lg">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-slate-50 mb-2 line-clamp-2">{article.title}</h3>
                    <p className="text-gray-600 dark:text-slate-400 text-sm line-clamp-2">{article.excerpt}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog">
              <Button variant="outline" className="group">
                Read All Articles
                <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg">Find answers to common questions</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="border rounded-lg px-6 bg-blue-50 dark:bg-slate-800 dark:border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-medium text-gray-900 dark:text-slate-50">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 dark:text-slate-300 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Appointment Booking Section */}
      <section id="book-appointment" className="py-20 px-6 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40">
              <Calendar className="w-3 h-3 mr-1" />
              Appointment
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Book an Appointment
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg max-w-2xl mx-auto">
              Choose between an in-person visit or an online video consultation
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <ConsultationPreparation />
            <AppointmentBooking />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-blue-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-50 mb-4">
              Contact Us
            </h2>
            <p className="text-gray-600 dark:text-slate-300 text-lg">Get in touch or visit us at the clinic</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Hospital className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    {doctorInfo.hospitalName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-600 dark:text-slate-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-50">Address</p>
                      <p className="text-gray-600 dark:text-slate-300">{doctorInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-600 dark:text-slate-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-50">Phone</p>
                      <a href={`tel:${doctorInfo.phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                        {doctorInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-600 dark:text-slate-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-50">Email</p>
                      <a href={`mailto:${doctorInfo.email}`} className="text-blue-600 dark:text-blue-400 hover:underline break-all">
                        {doctorInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Working Hours */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    Clinic Timings
                  </CardTitle>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Monday to Saturday</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="font-medium text-gray-900 dark:text-slate-50">In-Person</span>
                    <span className="text-gray-600 dark:text-slate-300">5:00 PM – 8:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="font-medium text-gray-900 dark:text-slate-50">Online</span>
                    <span className="text-gray-600 dark:text-slate-300">9:00 PM – 9:30 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-medium text-gray-900 dark:text-slate-50">Sunday</span>
                    <span className="text-red-500 dark:text-red-400 font-medium">Closed</span>
                  </div>
                </CardContent>
              </Card>

              {/* Google Review */}
              <a
                href="https://www.google.com/search?sca_esv=73000156de4ea350&rlz=1C1VDKB_enIN1062IN1064&sxsrf=ANbL-n6CxAqRf_1s5hYGhSoUIPBUat5ydw:1772556002807&kgmid=/g/11y_h8j9x8&q=Dr+Akshay+Kawedia+%7C+Pulmonologist+%7CM.D+%7C+D.M+(AIIMS,+Delhi)&shem=dlvs1,shrtsdl&shndl=30&source=sh/x/loc/uni/m1/1&kgs=42821b199ca27b6a&utm_source=dlvs1,shrtsdl,sh/x/loc/uni/m1/1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                data-testid="google-review-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Write a Google Review</span>
              </a>
            </div>

            {/* Contact Form */}
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
